import type { SceneGroup } from '@lumen5/luminary';

import { setSceneOverrides } from 'js/src/data/user-overrides/user-overrides-actions';
import { getUserOverrides } from 'js/src/data/user-overrides/user-overrides-selectors';
import type { AppDispatch, GetRootState } from 'js/src/libs/RootState';

/**
 * When we duplicate a scene group we want to duplicate over any user overrides for any scenes in the scene group
 * to ensure we don't make undesired changes.
 */
export const copyUserOverridesForDuplicatedSceneGroup = (
    newSceneGroup: SceneGroup,
    sourceSceneGroup: SceneGroup,
    systemTriggered: boolean,
    parentActionGroup: string | undefined,
) => {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        const newSceneIds = newSceneGroup.scenes.map((scene) => scene.id);
        const sourceSceneIds = sourceSceneGroup.scenes.map((scene) => scene.id);
        if (newSceneIds.length === 0 || sourceSceneIds.length === 0) {
            return;
        }
        if (newSceneIds.length !== sourceSceneIds.length) {
            throw new Error('Scene group length mismatch');
        }

        const userOverrides = getUserOverrides(getState());
        if (!userOverrides) {
            return;
        }

        // Copy over the overrides for the scenes in the new scene group
        Object.keys(userOverrides).forEach((sceneId) => {
            if (sourceSceneIds.includes(sceneId)) {
                const sceneIndex = sourceSceneIds.indexOf(sceneId);
                const newSceneId = newSceneIds[sceneIndex]; // get corresponding new scene id
                const sourceScene = sourceSceneGroup.scenes[sceneIndex];
                const newScene = newSceneGroup.scenes[sceneIndex];
                const overrides = userOverrides[sceneId];

                // asset_timing_overrides references asset ids from the source scene, but
                // duplicating a scene group regenerates every asset's id (see
                // replaceSceneGroupOperation's generateNewAssetIds calls), so remap by asset
                // name to the corresponding asset in the new scene -- the same approach
                // replaceSlideOperation uses. Otherwise the stale id survives (luminary.getAssetById
                // still resolves it globally, to the original asset in the source scene) until
                // setAssetIntroExitTimesOperation's scene-scoped lookup fails and throws
                // "Asset not found in scene".
                const assetTimingOverrides = (overrides.asset_timing_overrides || [])
                    .map((assetId) => {
                        const sourceAsset = sourceScene.getAssetById(assetId);
                        if (!sourceAsset) {
                            return undefined;
                        }
                        return newScene.getAssetByName(sourceAsset.name)?.id;
                    })
                    .filter((assetId): assetId is string => Boolean(assetId));

                dispatch(
                    setSceneOverrides({
                        sceneId: newSceneId,
                        overrides: { ...overrides, asset_timing_overrides: assetTimingOverrides },
                        systemTriggered,
                        parentActionGroup,
                    }),
                );
            }
        });
    };
};
